import { useState } from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import { useRole } from '@/hooks/useRole';
import { useCurrentStaff } from '@/hooks/useCurrentStaff';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface ClientOption {
  id: string;
  name: string;
  status: string;
}

function useClientOptions() {
  const { profile } = useAuth();
  const { isManager } = useRole();
  const { staff } = useCurrentStaff();

  return useQuery({
    queryKey: ['client_options', profile?.role, staff?.id],
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<ClientOption[]> => {
      if (isManager) {
        // Admin/Gerente vê todos os clientes ativos
        const { data } = await supabase.from('clients').select('id, name, status').order('name');
        return data ?? [];
      }
      if (staff?.id) {
        // Consultor/Comercial vê apenas clientes atribuídos
        const { data } = await supabase
          .from('staff_clinance_access')
          .select('clients(id, name, status)')
          .eq('staff_id', staff.id);
        return (data ?? [])
          .map((r) => r.clients as ClientOption | null)
          .filter((c): c is ClientOption => c !== null);
      }
      return [];
    },
    enabled: isManager || Boolean(staff?.id),
  });
}

export function ClientSwitcher() {
  const [open, setOpen] = useState(false);
  const { clientId, client, setClientId } = useCurrentClient();
  const { data: options = [] } = useClientOptions();

  const select = (id: string) => {
    setClientId(id === clientId ? null : id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-[220px] justify-between border-border bg-white px-3 text-sm font-normal"
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 size={15} className="flex-shrink-0 text-muted-foreground" />
            <span className="truncate">{client ? client.name : 'Selecionar cliente…'}</span>
          </span>
          <ChevronsUpDown size={14} className="flex-shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar cliente…" />
          <CommandList>
            <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.name}
                  onSelect={() => select(opt.id)}
                  className="flex items-center gap-2"
                >
                  <Check
                    size={14}
                    className={cn(
                      'flex-shrink-0',
                      opt.id === clientId ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="flex-1 truncate">{opt.name}</span>
                  <span
                    className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      opt.status === 'ativo'
                        ? 'bg-green-100 text-green-700'
                        : opt.status === 'lead'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {opt.status}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
